// Claude: New component — two more admin-only functions requested directly by the user: "bulk
// Claude: upload from csv for new terms (from lists created elsewhere). As with the bulk creation
// Claude: of users, provide a downloadable template, and use the success-driven approach. The
// Claude: second is an export to csv capability with filters. The export should include all
// Claude: fields of the terms selected." Backend: /v1/admin-terms/... (see
// Claude: admin_terms_csv_template/admin_bulk_upload_terms/admin_export_terms_csv in views.py).
// Claude: Role gating happens server-side (every endpoint is IsAdministrator-only) — this route is
// Claude: reachable by anyone logged in client-side, same pattern as /user-admin and the rest.
import React, { useState, useContext } from "react";
import Papa from "papaparse";
import languageMapper, { baseUrl, getValidAccessToken, mapKnownBackendError } from "../shared";
import Logo from "../logo512.png";
import Navbar from "../navbar";
import Navbaranonuser from "../navbaranonuser";
import { AuthContext } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "react-modal";
import "../i18n";

// Claude: same field list ADMIN_TERMS_CSV_FIELDS in views.py names (extra/reordered CSV columns
// Claude: are still fine — Papa Parse's header:true mode reads by name, not position).
const ADMIN_TERMS_CSV_FIELDS = [
  "word", "language", "wordEnglish", "pos", "definition", "example",
  "attribution", "field", "school", "synonyms", "status",
  // Claude: optional notes column (alpha-tester request) — an older CSV without it still uploads.
  "notes",
];

// Claude: matches ADMIN_TERMS_UPLOAD_CHUNK_SIZE in views.py — see that constant's comment for why
// this size. Bug fixed directly: "bulk uploading of terms with a test csv with 100 terms fails
// with 'An error occurred'" — a single request for a large CSV could take long enough to trip
// gunicorn/nginx's timeout with no way to show progress in the meantime. The CSV is now parsed
// client-side and uploaded in chunks of this size instead of as one file upload, which also
// removes any practical ceiling on how large a file can be imported.
const UPLOAD_CHUNK_SIZE = 100;

// Claude: parses a CSV's text into row objects the same shape admin_bulk_upload_terms's CSV
// DictReader path produces server-side: every field stripped, a fully-blank row dropped (a common
// trailing-newline artifact), and a `row` number matching what opening the file in a spreadsheet
// would show (header is row 1, so the first data row is row 2) — kept even for blank/dropped rows
// so a later row's number still matches its real position in the file. skipEmptyLines is
// deliberately false so Papa doesn't shift that numbering by silently dropping blank lines itself.
function parseTermsCsv(text) {
  const parsed = Papa.parse(text, { header: true, skipEmptyLines: false });
  const fields = parsed.meta.fields || [];
  if (!fields.includes("word")) {
    return { error: "CSV must include a 'word' column." };
  }
  const rows = [];
  parsed.data.forEach((raw, index) => {
    const row = { row: index + 2 };
    let hasValue = false;
    ADMIN_TERMS_CSV_FIELDS.forEach((key) => {
      const value = (raw[key] || "").toString().trim();
      row[key] = value;
      if (value) {
        hasValue = true;
      }
    });
    if (hasValue) {
      rows.push(row);
    }
  });
  return { rows };
}

// Claude: FileReader, not File.prototype.text() — the latter isn't implemented by jsdom's File/Blob
// Claude: polyfill (used by this project's Jest test environment), and FileReader is supported
// Claude: everywhere text() would be anyway.
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

// Claude: same 13-language code list MissingTerms.js/ReviewQueue.js already use for a language
// Claude: filter (Word.language's fixed choices).
const LANGUAGE_OPTIONS = [
  { code: "Eng", label: "English" },
  { code: "Afr", label: "Afrikaans" },
  { code: "Zul", label: "isiZulu" },
  { code: "Ses", label: "Sesotho" },
  { code: "Sep", label: "Sepedi" },
  { code: "Set", label: "Setswana" },
  { code: "Xit", label: "Xitsonga" },
  { code: "Sis", label: "siSwati" },
  { code: "Nde", label: "isiNdebele" },
  { code: "Xho", label: "isiXhosa" },
  { code: "Tsh", label: "Tshivenḓa" },
  { code: "Por", label: "Portuguese" },
  { code: "Fre", label: "French" },
];

const STATUS_OPTIONS = [
  { code: "ACC", labelKey: "accepted" },
  { code: "PND", labelKey: "pending" },
  { code: "REJ", labelKey: "rejected" },
  { code: "FLG", labelKey: "flagged" },
];

// Claude: alpha-tester feedback — "more extensive search/filtering capabilities... on the user who
// added terms, on dates... the ability to bulk delete on terms filtered." Matches
// ADMIN_TERMS_SEARCH_DEFAULT_LIMIT in views.py.
const SEARCH_PAGE_SIZE = 50;

const REPORT_PERIOD_OPTIONS = [
  { code: "day", labelKey: "report_period_day" },
  { code: "week", labelKey: "report_period_week" },
  { code: "month", labelKey: "report_period_month" },
];

// Claude: fetches a CSV-producing endpoint (with the Authorization header it requires — a plain
// Claude: <a href> can't carry one) and hands it to the browser as a download. Shared by the
// Claude: template download and the export, which differ only in URL and filename. `t` is passed
// Claude: in (rather than called via useTranslation() here) since this is a plain module-level
// Claude: function, not a component — every call site already has `t` in scope.
async function downloadCsv(url, filename, t) {
  const token = await getValidAccessToken();
  const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) {
    alert(t("could_not_download_file"));
    return;
  }
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

const TermAdmin = () => {
  const { auth } = useContext(AuthContext);
  const { t } = useTranslation();

  const [csvFile, setCsvFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  // Claude: alpha-tester feedback — "a duplicate check should be included -- if all terms are
  // Claude: duplicates, a warning that this is probably a duplication of import (with options
  // Claude: 'Replace previous import', 'Add in any case', and 'Cancel')." Holds the backend's
  // Claude: {duplicate_count, total_rows} while the modal below is open; csvFile/parsedRows are
  // Claude: deliberately NOT cleared in this case (unlike a normal successful upload) so
  // Claude: "Replace"/"Add" can resubmit the same parsed rows without re-parsing the file.
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  // Claude: the CSV parsed once (see parseTermsCsv) and reused across the precheck call and every
  // Claude: chunk of the actual upload — and, if the duplicate-warning modal appears, across the
  // Claude: follow-up "Replace"/"Add" call too, so the file is never re-parsed mid-flow.
  const [parsedRows, setParsedRows] = useState(null);
  // Claude: alpha-tester feedback — "is it possible to give an indication of progress instead of
  // Claude: (or in addition to) the spinner?" — null while nothing is uploading, otherwise
  // Claude: {completed, total} in terms of rows, updated after each chunk resolves.
  const [uploadProgress, setUploadProgress] = useState(null);

  const [languageFilter, setLanguageFilter] = useState("");
  const [statusFilters, setStatusFilters] = useState([]);
  const [wordFilter, setWordFilter] = useState("");
  const [wordEnglishFilter, setWordEnglishFilter] = useState("");
  const [editorFilter, setEditorFilter] = useState("");
  // Claude: alpha-tester feedback — "more extensive search/filtering capabilities... on dates."
  // Claude: date_created is when a term was first added (immutable); date_edited is when it was
  // Claude: last changed by anyone — see both fields' comments in models.py/views.py.
  const [dateCreatedFrom, setDateCreatedFrom] = useState("");
  const [dateCreatedTo, setDateCreatedTo] = useState("");
  const [dateEditedFrom, setDateEditedFrom] = useState("");
  const [dateEditedTo, setDateEditedTo] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  // Claude: alpha-tester feedback — "there should be a way to see what a filter matches" before
  // Claude: exporting/deleting it. searchResults is null until a search has actually been run
  // Claude: (distinct from an empty array, which means a search ran and matched nothing) — the
  // Claude: bulk-delete button below is gated on this so an administrator can't delete against a
  // Claude: filter they haven't actually previewed.
  const [searchResults, setSearchResults] = useState(null);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchOffset, setSearchOffset] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Claude: alpha-tester feedback — "the ability to bulk delete on terms filtered." The confirm
  // Claude: modal requires typing the exact match count before enabling the delete button — see
  // Claude: admin_bulk_delete_terms's docstring in views.py for why confirm_count is re-validated
  // Claude: server-side too, not just trusted from this client-side gate.
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteConfirmInput, setBulkDeleteConfirmInput] = useState("");
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteError, setBulkDeleteError] = useState(null);

  // Claude: alpha-tester feedback — "the ability to create reports -- terms added per period, per
  // Claude: language, etc." Deliberately its own filter state, separate from the search/export/
  // Claude: bulk-delete filters above — a report is a broader "how has the database grown"
  // Claude: question, not tied to whatever narrow filter an administrator currently has set for
  // Claude: finding specific terms to act on.
  const [reportPeriod, setReportPeriod] = useState("month");
  const [reportStatusFilters, setReportStatusFilters] = useState([]);
  const [reportDateFrom, setReportDateFrom] = useState("");
  const [reportDateTo, setReportDateTo] = useState("");
  const [reportData, setReportData] = useState(null);
  const [isRunningReport, setIsRunningReport] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const toggleStatusFilter = (code) => {
    setStatusFilters((previous) =>
      previous.includes(code) ? previous.filter((c) => c !== code) : [...previous, code]
    );
  };

  const toggleReportStatusFilter = (code) => {
    setReportStatusFilters((previous) =>
      previous.includes(code) ? previous.filter((c) => c !== code) : [...previous, code]
    );
  };

  // Claude: shared by handleExport, fetchSearchResults and confirmBulkDelete — the same filter set
  // (see _filter_admin_terms_queryset in views.py) so all three act on exactly what's currently
  // shown/typed in the Search / Export / Bulk delete section's filter panel.
  const buildTermFilterParams = () => {
    const params = new URLSearchParams();
    if (languageFilter) params.set("language", languageFilter);
    if (statusFilters.length > 0) params.set("statuses", statusFilters.join(","));
    if (wordFilter) params.set("word", wordFilter);
    if (wordEnglishFilter) params.set("wordEnglish", wordEnglishFilter);
    if (editorFilter) params.set("editor", editorFilter);
    if (dateCreatedFrom) params.set("date_created_from", dateCreatedFrom);
    if (dateCreatedTo) params.set("date_created_to", dateCreatedTo);
    if (dateEditedFrom) params.set("date_edited_from", dateEditedFrom);
    if (dateEditedTo) params.set("date_edited_to", dateEditedTo);
    return params;
  };

  // Claude: alpha-tester feedback — "there should be a way to see what a filter matches." Also
  // backs the bulk-delete button/modal below, which reuses this same result (searchTotal) as the
  // count an administrator confirms before deleting.
  const fetchSearchResults = async (offset = 0) => {
    setIsSearching(true);
    setSearchError(null);
    const token = await getValidAccessToken();
    const params = buildTermFilterParams();
    params.set("limit", SEARCH_PAGE_SIZE);
    params.set("offset", offset);
    try {
      const response = await fetch(baseUrl + `v1/admin-terms/search/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) {
        setSearchError(mapKnownBackendError(t, data.error) || t("search_failed"));
        setIsSearching(false);
        return;
      }
      setSearchResults(data.results);
      setSearchTotal(data.total);
      setSearchOffset(offset);
    } catch (err) {
      setSearchError(t("search_failed"));
    }
    setIsSearching(false);
  };

  const openBulkDeleteModal = () => {
    setBulkDeleteConfirmInput("");
    setBulkDeleteError(null);
    setBulkDeleteModalOpen(true);
  };

  // Claude: confirm_count is searchTotal — the count from the administrator's own last search,
  // which they've just typed back to confirm. admin_bulk_delete_terms re-validates this against
  // the live count server-side regardless (see its docstring) — a mismatch there means the
  // matching set changed since this search, not that this client-side value was wrong.
  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    setBulkDeleteError(null);
    const token = await getValidAccessToken();
    const params = buildTermFilterParams();
    try {
      const response = await fetch(baseUrl + `v1/admin-terms/bulk-delete/?${params.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirm_count: searchTotal }),
      });
      const data = await response.json();
      if (!response.ok) {
        setBulkDeleteError(mapKnownBackendError(t, data.error) || t("delete_failed"));
        setIsBulkDeleting(false);
        return;
      }
      setBulkDeleteModalOpen(false);
      setSearchResults(null);
      setSearchTotal(0);
      alert(data.message);
    } catch (err) {
      setBulkDeleteError(t("delete_failed"));
    }
    setIsBulkDeleting(false);
  };

  const buildReportParams = (extra = {}) => {
    const params = new URLSearchParams();
    params.set("period", reportPeriod);
    if (reportStatusFilters.length > 0) params.set("statuses", reportStatusFilters.join(","));
    if (reportDateFrom) params.set("date_created_from", reportDateFrom);
    if (reportDateTo) params.set("date_created_to", reportDateTo);
    Object.entries(extra).forEach(([key, value]) => params.set(key, value));
    return params;
  };

  // Claude: alpha-tester feedback — "the ability to create reports -- terms added per period, per
  // language, etc."
  const runReport = async () => {
    setIsRunningReport(true);
    const token = await getValidAccessToken();
    try {
      const response = await fetch(baseUrl + `v1/admin-terms/report/?${buildReportParams().toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setReportData(data);
      } else {
        alert(mapKnownBackendError(t, data.error) || t("could_not_run_report"));
      }
    } catch (err) {
      alert(t("could_not_run_report"));
    }
    setIsRunningReport(false);
  };

  const downloadReportCsv = async () => {
    setIsDownloadingReport(true);
    // Claude: `output`, not `format` — DRF reserves ?format= for its own content negotiation (see
    // admin_report_terms's comment in views.py).
    await downloadCsv(
      baseUrl + `v1/admin-terms/report/?${buildReportParams({ output: "csv" }).toString()}`,
      "terminology_report.csv",
      t
    );
    setIsDownloadingReport(false);
  };

  // Claude: uploads `rows` in UPLOAD_CHUNK_SIZE-sized chunks, sequentially (not in parallel — keeps
  // Claude: memory/DB load on the small VPS bounded and progress reporting straightforward),
  // Claude: updating uploadProgress after each one resolves. Stops on the first failed chunk rather
  // Claude: than continuing past it, but keeps (and returns) whatever succeeded before that so the
  // Claude: administrator isn't left with no information about a large upload that got partway
  // Claude: through.
  const uploadRowsInChunks = async (rows, onDuplicate) => {
    let created = [];
    let errors = [];
    for (let start = 0; start < rows.length; start += UPLOAD_CHUNK_SIZE) {
      const chunk = rows.slice(start, start + UPLOAD_CHUNK_SIZE);
      let response;
      try {
        // Claude: bugfix reported directly — a 401 partway through a large upload. Fetching the
        // Claude: token once before this loop and reusing it for every chunk meant a large enough
        // Claude: file (many chunks, each a real round trip) could outlast the access token's
        // Claude: lifetime before the loop finished, since only getValidAccessToken() itself
        // Claude: refreshes an expired token — called once up front, it never got the chance to on
        // Claude: a later chunk. Calling it fresh before every chunk fixes that.
        const token = await getValidAccessToken();
        response = await fetch(baseUrl + "v1/admin-terms/bulk-upload/batch/", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ rows: chunk, on_duplicate: onDuplicate || "" }),
        });
      } catch (err) {
        alert(t("upload_interrupted", { completed: start, total: rows.length }));
        return { created, errors };
      }
      const data = await response.json();
      if (!response.ok) {
        alert(`Error: ${data.error}`);
        return { created, errors };
      }
      created = created.concat(data.created);
      errors = errors.concat(data.errors);
      setUploadProgress({ completed: Math.min(start + UPLOAD_CHUNK_SIZE, rows.length), total: rows.length });
    }
    return { created, errors };
  };

  // Claude: onDuplicate is undefined for the administrator's own first attempt (let the backend
  // Claude: decide whether to warn) and 'replace'/'add' when resubmitting after the modal below.
  // Claude: Bugfix — "bulk uploading of terms with a test csv with 100 terms fails with 'An error
  // Claude: occurred'": the CSV is now parsed client-side (parseTermsCsv) and uploaded in chunks
  // Claude: (uploadRowsInChunks) instead of as one file upload, removing the practical size ceiling
  // Claude: a single request's timeout used to impose, and giving real per-chunk progress instead
  // Claude: of only a spinner.
  const handleCsvUpload = async (onDuplicate) => {
    if (!csvFile && !parsedRows) {
      return;
    }
    setIsUploading(true);
    setUploadResult(null);
    setDuplicateWarning(null);

    let rows = parsedRows;
    if (!rows) {
      const text = await readFileAsText(csvFile);
      const parsed = parseTermsCsv(text);
      if (parsed.error) {
        alert(parsed.error);
        setIsUploading(false);
        return;
      }
      rows = parsed.rows;
      setParsedRows(rows);
    }

    if (!onDuplicate) {
      const token = await getValidAccessToken();
      try {
        const precheckResponse = await fetch(baseUrl + "v1/admin-terms/bulk-upload/precheck/", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ rows }),
        });
        const precheckData = await precheckResponse.json();
        if (!precheckResponse.ok) {
          alert(`Error: ${precheckData.error}`);
          setIsUploading(false);
          return;
        }
        if (precheckData.duplicate_warning) {
          setDuplicateWarning(precheckData);
          setIsUploading(false);
          return;
        }
      } catch (err) {
        alert(t("generic_error"));
        setIsUploading(false);
        return;
      }
    }

    setUploadProgress({ completed: 0, total: rows.length });
    // Claude: always clear the chosen file/parsed rows here, even on an interrupted upload — a
    // Claude: retry that reused them would resubmit rows already successfully created, silently
    // Claude: duplicating them, since there's no per-row "already done" tracking across chunks.
    // Claude: uploadRowsInChunks' alert already tells the administrator how far it got, so they can
    // Claude: prepare a follow-up file with just the remaining rows if needed.
    const { created, errors } = await uploadRowsInChunks(rows, onDuplicate);
    setUploadResult({ created, errors });
    setUploadProgress(null);
    setIsUploading(false);
    setCsvFile(null);
    setParsedRows(null);
  };

  const cancelDuplicateImport = () => {
    setDuplicateWarning(null);
    setCsvFile(null);
    setParsedRows(null);
  };

  const handleExport = async () => {
    setIsExporting(true);
    await downloadCsv(
      baseUrl + `v1/admin-terms/export/?${buildTermFilterParams().toString()}`,
      "terminology_export.csv",
      t
    );
    setIsExporting(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {auth.userName === null && <Navbaranonuser />}
      {auth.userName != null && <Navbar />}

      <div className="flex flex-col justify-center items-center p-2">
        <h3 className="text-4xl font-bold mb-6">
          <Link to={"/"}>{t("term_admin")}</Link>
        </h3>

        {/* Bulk upload from CSV */}
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-lg p-6 mb-6">
          <h4 className="text-xl font-semibold mb-4">{t("bulk_upload_terms")}</h4>
          <p className="mb-4 text-gray-700">{t("bulk_upload_terms_help")}</p>
          <button
            type="button"
            onClick={() => downloadCsv(baseUrl + "v1/admin-terms/csv-template/", "term_upload_template.csv", t)}
            className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800 mb-4"
          >
            {t("download_csv_template")}
          </button>
          <div className="mb-4">
            <input
              type="file"
              accept=".csv"
              aria-label={t("choose_csv_file")}
              onChange={(e) => {
                setCsvFile(e.target.files[0] || null);
                setParsedRows(null);
              }}
              className="border p-2 w-full"
            />
          </div>
          <button
            type="button"
            onClick={() => handleCsvUpload()}
            disabled={!csvFile || isUploading}
            className="bg-cyan-700 text-white py-2 px-4 rounded-lg hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? t("uploading") : t("upload_csv")}
          </button>

          {/* Claude: alpha-tester feedback — "is it possible to give an indication of progress
              instead of (or in addition to) the spinner?" Shown only while chunks are actually being
              uploaded (not during the client-side parse/precheck step, which is fast enough not to
              need it). A real progress bar rather than the export button's indeterminate
              animate-spin dot below, since here — unlike the export — there's a meaningful "how much
              is left" number to show. */}
          {uploadProgress && (
            <div className="mt-3">
              <div className="w-full bg-gray-200 rounded-full h-2.5" role="progressbar"
                aria-valuenow={uploadProgress.completed} aria-valuemin={0} aria-valuemax={uploadProgress.total}>
                <div
                  className="bg-cyan-700 h-2.5 rounded-full transition-all"
                  style={{ width: `${Math.round((uploadProgress.completed / uploadProgress.total) * 100)}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {t("upload_progress", { completed: uploadProgress.completed, total: uploadProgress.total })}
              </p>
            </div>
          )}

          {uploadResult && (
            <div className="mt-4">
              <h5 className="font-semibold">{t("csv_upload_results")}</h5>
              <p>{t("terms_created", { count: uploadResult.created.length })}</p>
              {uploadResult.created.length > 0 && (
                <table className="border-separate [border-spacing:0.5rem]">
                  <thead>
                    <tr>
                      <th>{t("word")}</th>
                      <th>{t("english")}</th>
                      <th>{t("language")}</th>
                      <th>{t("status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.created.map((row) => (
                      <tr key={row.row}>
                        <td>{row.word}</td>
                        <td>{row.wordEnglish}</td>
                        <td>{row.language}</td>
                        <td>{t(STATUS_OPTIONS.find((o) => o.code === row.status)?.labelKey || row.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {uploadResult.errors.length > 0 && (
                <>
                  <p className="mt-2">{t("rows_skipped", { count: uploadResult.errors.length })}</p>
                  <table className="border-separate [border-spacing:0.5rem]">
                    <thead>
                      <tr>
                        <th>{t("row_label")}</th>
                        <th>{t("word")}</th>
                        <th>{t("error_label")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.errors.map((row) => (
                        <tr key={row.row}>
                          <td>{row.row}</td>
                          <td>{row.word || "—"}</td>
                          <td>{row.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}

          {/* Claude: alpha-tester feedback — "if all terms are duplicates, a warning that this is
              probably a duplication of import." Modelled on the existing NoResultsModal.js/
              DuplicateCleanup.js pattern (react-modal, same appElement/className convention). */}
          <Modal
            isOpen={duplicateWarning !== null}
            onRequestClose={cancelDuplicateImport}
            appElement={document.getElementById("root")}
            className="modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            {duplicateWarning && (
              <div className="bg-white rounded-lg shadow-lg w-11/12 md:w-5/12 p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{t("duplicate_warning_title")}</h3>
                <p className="mb-4 text-gray-700">
                  {t("duplicate_warning_message", { count: duplicateWarning.duplicate_count })}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleCsvUpload("replace")}
                    className="bg-cyan-700 text-white py-2 px-4 rounded-lg hover:bg-cyan-800"
                  >
                    {t("replace_previous_import")}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleCsvUpload("add")}
                    className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800"
                  >
                    {t("add_in_any_case")}
                  </button>
                  <button
                    type="button"
                    onClick={cancelDuplicateImport}
                    className="bg-gray-300 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-400"
                  >
                    {t("cancel")}
                  </button>
                </div>
              </div>
            )}
          </Modal>
        </div>

        {/* Search / Export / Bulk delete — shared filters */}
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-lg p-6">
          <h4 className="text-xl font-semibold mb-4">{t("search_export_terms")}</h4>
          <p className="mb-4 text-gray-700">{t("export_terms_help")}</p>

          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label htmlFor="term-admin-language-filter" className="block text-sm text-gray-700">
                {t("filter_language")}
              </label>
              <select
                id="term-admin-language-filter"
                value={languageFilter}
                onChange={(e) => setLanguageFilter(e.target.value)}
                className="border p-2 rounded-lg"
              >
                <option value="">{t("all_languages_filter")}</option>
                {LANGUAGE_OPTIONS.map((lang) => (
                  <option key={lang.code} value={lang.code}>{lang.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="term-admin-word-filter" className="block text-sm text-gray-700">
                {t("word")}:
              </label>
              <input
                id="term-admin-word-filter"
                type="text"
                value={wordFilter}
                onChange={(e) => setWordFilter(e.target.value)}
                className="border p-2 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="term-admin-word-english-filter" className="block text-sm text-gray-700">
                {t("english")}:
              </label>
              <input
                id="term-admin-word-english-filter"
                type="text"
                value={wordEnglishFilter}
                onChange={(e) => setWordEnglishFilter(e.target.value)}
                className="border p-2 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="term-admin-editor-filter" className="block text-sm text-gray-700">
                {t("filter_submitted_by")}
              </label>
              <input
                id="term-admin-editor-filter"
                type="text"
                value={editorFilter}
                onChange={(e) => setEditorFilter(e.target.value)}
                className="border p-2 rounded-lg"
              />
            </div>
          </div>

          {/* Claude: alpha-tester feedback — "more extensive search/filtering capabilities...
              on dates." Both ranges are inclusive (see _filter_admin_terms_queryset in views.py). */}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label htmlFor="term-admin-date-created-from" className="block text-sm text-gray-700">
                {t("filter_date_created_from")}
              </label>
              <input
                id="term-admin-date-created-from"
                type="date"
                value={dateCreatedFrom}
                onChange={(e) => setDateCreatedFrom(e.target.value)}
                className="border p-2 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="term-admin-date-created-to" className="block text-sm text-gray-700">
                {t("filter_date_created_to")}
              </label>
              <input
                id="term-admin-date-created-to"
                type="date"
                value={dateCreatedTo}
                onChange={(e) => setDateCreatedTo(e.target.value)}
                className="border p-2 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="term-admin-date-edited-from" className="block text-sm text-gray-700">
                {t("filter_date_edited_from")}
              </label>
              <input
                id="term-admin-date-edited-from"
                type="date"
                value={dateEditedFrom}
                onChange={(e) => setDateEditedFrom(e.target.value)}
                className="border p-2 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="term-admin-date-edited-to" className="block text-sm text-gray-700">
                {t("filter_date_edited_to")}
              </label>
              <input
                id="term-admin-date-edited-to"
                type="date"
                value={dateEditedTo}
                onChange={(e) => setDateEditedTo(e.target.value)}
                className="border p-2 rounded-lg"
              />
            </div>
          </div>

          <div className="mb-4">
            <span className="block text-sm text-gray-700 mb-1">{t("status")}</span>
            {STATUS_OPTIONS.map((option) => (
              <label key={option.code} className="inline-flex items-center mr-4">
                <input
                  type="checkbox"
                  checked={statusFilters.includes(option.code)}
                  onChange={() => toggleStatusFilter(option.code)}
                  className="form-checkbox h-4 w-4 text-cyan-700 accent-cyan-700"
                />
                <span className="ml-1">{t(option.labelKey)}</span>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => fetchSearchResults(0)}
              disabled={isSearching}
              className="bg-cyan-700 text-white py-2 px-4 rounded-lg hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSearching ? t("searching") : t("search_terms")}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting}
              className="bg-cyan-700 text-white py-2 px-4 rounded-lg hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? t("exporting") : t("export_csv")}
            </button>
            {/* Claude: UAT feedback — "the sorting takes almost 10 seconds, and with a foreseen
                increase in the size of the database, that will increase. A spinner will be
                helpful." Sorting the filtered terms happens in Python, not the database (see
                admin_export_terms_csv in views.py — that's the actual fix for the crash this
                replaced), so its cost scales with how many rows match the filters, same reasoning
                the comment there gives; the button's text already changed to "Exporting..." but a
                spinner reads as "something is happening" faster than text alone. Same
                animate-spin pattern LandingPage.js's own search spinner already uses. */}
            {isExporting && (
              <div
                className="inline-block h-6 w-6 animate-spin rounded-full text-cyan-700 border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                role="status"
              >
                <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                  {t("exporting")}
                </span>
              </div>
            )}
          </div>

          {searchError && <p className="mt-4 text-rose-700">{searchError}</p>}

          {/* Claude: alpha-tester feedback — "there should be a way to see what a filter matches"
              before exporting/deleting it. Only rendered once a search has actually run
              (searchResults !== null) — an empty array (a real search that matched nothing) still
              renders the "0 results" state below, distinct from never having searched at all. */}
          {searchResults !== null && (
            <div className="mt-4">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <p className="text-gray-700">
                  {searchTotal > 0
                    ? t("showing_range", {
                        start: searchOffset + 1,
                        end: searchOffset + searchResults.length,
                        total: searchTotal,
                      })
                    : t("no_matching_terms")}
                </p>
                {searchTotal > 0 && (
                  <button
                    type="button"
                    onClick={openBulkDeleteModal}
                    className="bg-rose-700 text-white py-2 px-4 rounded-lg hover:bg-rose-800"
                  >
                    {t("delete_matching_terms", { count: searchTotal })}
                  </button>
                )}
              </div>
              {searchResults.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="border-separate [border-spacing:0.5rem]">
                    <thead>
                      <tr>
                        <th>{t("word")}</th>
                        <th>{t("english")}</th>
                        <th>{t("language")}</th>
                        <th>{t("status")}</th>
                        <th>{t("filter_submitted_by")}</th>
                        <th>{t("date_added")}</th>
                        <th>{t("date_last_edited")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((row) => (
                        <tr key={row.id}>
                          <td>{row.word}</td>
                          <td>{row.wordEnglish}</td>
                          <td>{languageMapper(row.language)}</td>
                          <td>{t(STATUS_OPTIONS.find((o) => o.code === row.status)?.labelKey || row.status)}</td>
                          <td>{row.editor}</td>
                          <td>{row.date_created}</td>
                          <td>{row.date_edited}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {(searchOffset > 0 || searchOffset + SEARCH_PAGE_SIZE < searchTotal) && (
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => fetchSearchResults(Math.max(0, searchOffset - SEARCH_PAGE_SIZE))}
                    disabled={searchOffset === 0 || isSearching}
                    className="bg-gray-700 text-white py-1 px-3 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("previous")}
                  </button>
                  <button
                    type="button"
                    onClick={() => fetchSearchResults(searchOffset + SEARCH_PAGE_SIZE)}
                    disabled={searchOffset + SEARCH_PAGE_SIZE >= searchTotal || isSearching}
                    className="bg-gray-700 text-white py-1 px-3 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("next")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Claude: alpha-tester feedback — "the ability to bulk delete on terms filtered," for
              cleaning up alpha testers' own test data and inappropriate submissions once "access is
              relatively open." Typing the exact count is a deliberately higher-friction confirm
              than the window.confirm this app uses elsewhere (e.g. DuplicateCleanup.js's "Merge all
              exact matches") — this action can't be undone and can remove far more than one term. */}
          <Modal
            isOpen={bulkDeleteModalOpen}
            onRequestClose={() => setBulkDeleteModalOpen(false)}
            appElement={document.getElementById("root")}
            className="modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <div className="bg-white rounded-lg shadow-lg w-11/12 md:w-5/12 p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{t("bulk_delete_modal_title")}</h3>
              <p className="mb-4 text-gray-700">
                {t("bulk_delete_modal_warning", { count: searchTotal })}
              </p>
              <label htmlFor="bulk-delete-confirm-input" className="block text-sm text-gray-700 mb-1">
                {t("bulk_delete_confirm_label", { count: searchTotal })}
              </label>
              <input
                id="bulk-delete-confirm-input"
                type="text"
                value={bulkDeleteConfirmInput}
                onChange={(e) => setBulkDeleteConfirmInput(e.target.value)}
                className="border p-2 rounded-lg w-full mb-4"
              />
              {bulkDeleteError && <p className="mb-4 text-rose-700">{bulkDeleteError}</p>}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={confirmBulkDelete}
                  disabled={bulkDeleteConfirmInput !== String(searchTotal) || isBulkDeleting}
                  className="bg-rose-700 text-white py-2 px-4 rounded-lg hover:bg-rose-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBulkDeleting ? t("deleting") : t("bulk_delete_confirm_button")}
                </button>
                <button
                  type="button"
                  onClick={() => setBulkDeleteModalOpen(false)}
                  className="bg-gray-300 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-400"
                >
                  {t("cancel")}
                </button>
              </div>
            </div>
          </Modal>
        </div>

        {/* Claude: alpha-tester feedback — "the ability to create reports -- terms added per
            period, per language, etc." */}
        <div className="w-full max-w-3xl bg-white rounded-lg shadow-lg p-6 mt-6">
          <h4 className="text-xl font-semibold mb-4">{t("reports_heading")}</h4>
          <p className="mb-4 text-gray-700">{t("reports_help")}</p>

          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <label htmlFor="term-admin-report-period" className="block text-sm text-gray-700">
                {t("report_period_label")}
              </label>
              <select
                id="term-admin-report-period"
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
                className="border p-2 rounded-lg"
              >
                {REPORT_PERIOD_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>{t(option.labelKey)}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="term-admin-report-date-from" className="block text-sm text-gray-700">
                {t("filter_date_created_from")}
              </label>
              <input
                id="term-admin-report-date-from"
                type="date"
                value={reportDateFrom}
                onChange={(e) => setReportDateFrom(e.target.value)}
                className="border p-2 rounded-lg"
              />
            </div>
            <div>
              <label htmlFor="term-admin-report-date-to" className="block text-sm text-gray-700">
                {t("filter_date_created_to")}
              </label>
              <input
                id="term-admin-report-date-to"
                type="date"
                value={reportDateTo}
                onChange={(e) => setReportDateTo(e.target.value)}
                className="border p-2 rounded-lg"
              />
            </div>
          </div>

          <div className="mb-4">
            <span className="block text-sm text-gray-700 mb-1">{t("status")}</span>
            {STATUS_OPTIONS.map((option) => (
              <label key={option.code} className="inline-flex items-center mr-4">
                <input
                  type="checkbox"
                  checked={reportStatusFilters.includes(option.code)}
                  onChange={() => toggleReportStatusFilter(option.code)}
                  className="form-checkbox h-4 w-4 text-cyan-700 accent-cyan-700"
                />
                <span className="ml-1">{t(option.labelKey)}</span>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap mb-4">
            <button
              type="button"
              onClick={runReport}
              disabled={isRunningReport}
              className="bg-cyan-700 text-white py-2 px-4 rounded-lg hover:bg-cyan-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRunningReport ? t("running_report") : t("run_report")}
            </button>
            {reportData && (
              <button
                type="button"
                onClick={downloadReportCsv}
                disabled={isDownloadingReport}
                className="bg-gray-700 text-white py-2 px-4 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloadingReport ? t("exporting") : t("download_report_csv")}
              </button>
            )}
          </div>

          {reportData && (
            reportData.rows.length === 0 ? (
              <p className="text-gray-700">{t("report_no_data")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="border-separate [border-spacing:0.5rem]">
                  <thead>
                    <tr>
                      <th>{t("language")}</th>
                      {reportData.columns.map((column) => (
                        <th key={column}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.rows.map((language, index) => (
                      <tr key={language}>
                        <td>{languageMapper(language)}</td>
                        {reportData.table[index].map((count, columnIndex) => (
                          <td key={reportData.columns[columnIndex]}>{count}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
      <div className="flex flex-col justify-center items-center p-4">
        <Link to={"/"}>
          <img src={Logo} alt="Logo" width="100" height="100" />
        </Link>
      </div>
    </div>
  );
};

export default TermAdmin;
